import React, { useEffect, useRef, useState } from "react";
import Section from "../../components/Section";
import { pageTitle } from "../../helpers/PageTitle";
import axiosInstance from "../../utils/axiosInstance";
import { Link, useLocation, useParams } from "react-router-dom";
import { addPatient, editPatient, fetchAppointmentDetails, fetchMyUserDetails, fetchPatients, imageBaseUrl } from "../../constants/ApiConstants";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { linearTopGradient } from "../../constants/colors";
import toast from "react-hot-toast";
import FullPageLoader from "../../components/FullPageLoader";
import AppointmentTimeInfo from "../AppointmentSummary/comps/AppointmentTimeInfo";
import AppointmentOrderSummary from "../AppointmentSummary/comps/AppointmentOrderSummary";
import DoctorDetailHeader from "../../components/DoctorDetailHeader";
import SecondaryButton from "../../components/SecondaryButton";
import Rating from "../../components/Rating";
import PageHeader from "../../components/PageHeader";
import LoadMore from "../../components/LoadMore";
import MyModal from "../../components/MyModal";
import InputField from "../../components/InputField";
import SelectField from "../../components/SelectField";
import { FaCirclePlus } from "react-icons/fa6";
import CommonMobileHeader from "../../components/Header/CommonMobileHeader";

export default function ManagePatients() {
  pageTitle("Manage Patients");
  const { appointmentId } = useParams();
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const userId = useSelector((state) => state.appSlice.userData?.id);
  const [userDetail, setUserDetail] = useState(null);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPatientData, setEditPatientData] = useState(null);

  const addPatientFileRef = useRef();
  const editPatientFileRef = useRef();

  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [addPatientData, setAddPatientData] = useState({
    fullname: "",
    age: "",
    gender: 1,
    relation: "",
    selectedImage: "",
    selectedImagePreview: "",
  });

  const [submitting, setSubmitting] = useState(false);

  const rowLimit = 10;

  useEffect(() => {
    fetchUserDetail();
    fetchPatientsData();
  }, []);

  const fetchUserDetail = () => {
    setLoading(true);
    axiosInstance
      .post(fetchMyUserDetails, { user_id: userId })
      .then((res) => {
        setUserDetail(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const fetchPatientsData = () => {
    setLoading(true);
    axiosInstance
      .post(fetchPatients, {
        user_id: userId,
      })
      .then((res) => {
        setPatientData(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onEditPatientClick = (patient) => {
    setEditPatientData({ ...patient, selectedImagePreview: `${imageBaseUrl}/${patient.image}`, selectedImage: "" });
    setShowEditModal(true);
  };

  const onAddPatientClick = (patient) => {
    setShowAddPatientModal(true);
    setAddPatientData({
      fullname: "",
      age: "",
      gender: 1,
      relation: "",
      selectedImage: "",
      selectedImagePreview: "",
    });
  };

  const onDeletePatientClick = (patient) => {};

  const onAddPatientImageClick = (event) => {
    addPatientFileRef.current.click();
  };

  const onEditPatientImageClick = (event) => {
    editPatientFileRef.current.click();
  };

  const handleAddPatientImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setAddPatientData({ ...addPatientData, selectedImage: file, selectedImagePreview: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditPatientImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setEditPatientData({ ...editPatientData, selectedImage: file });
        setEditPatientData({ ...editPatientData, selectedImagePreview: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const saveNewPatient = () => {
    if (addPatientData?.fullname === "") {
      toast.error("Full name is required");
      return;
    }

    if (addPatientData?.age === "") {
      toast.error("Age is required");
      return;
    }

    if (addPatientData?.relation === "") {
      toast.error("Relation is required.");
      return;
    }
    setSubmitting(true);

    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("fullname", addPatientData?.fullname);
    formData.append("gender", addPatientData?.gender);
    formData.append("age", addPatientData?.age);
    formData.append("relation", addPatientData?.relation);
    if (addPatientData?.selectedImage && addPatientData?.selectedImage instanceof File) {
      formData.append("image", addPatientData?.selectedImage);
    }
    axiosInstance
      .post(addPatient, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data?.status) {
          toast.success("Patient addded succesfully.");
          setAddPatientData({ fullname: "", age: "", gender: 1, relation: "", selectedImage: "", selectedImagePreview: "" });
          setShowAddPatientModal(false);
        } else {
          toast.error(res.data?.message);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const updatePatient = () => {
    if (editPatientData?.fullname === "") {
      toast.error("Full name is required");
      return;
    }

    if (editPatientData?.age === "") {
      toast.error("Age is required");
      return;
    }

    if (editPatientData?.relation === "") {
      toast.error("Relation is required.");
      return;
    }
    setSubmitting(true);
    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("patient_id", editPatientData?.id);
    formData.append("fullname", editPatientData?.fullname);
    formData.append("gender", editPatientData?.gender);
    formData.append("age", editPatientData?.age);
    formData.append("relation", editPatientData?.relation);
    if (editPatientData?.selectedImage && editPatientData?.selectedImage instanceof File) {
      formData.append("image", editPatientData?.selectedImage);
    }
    axiosInstance
      .post(editPatient, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data?.status) {
          toast.success("Patient updated succesfully.");
          setEditPatientData({ fullname: "", age: "", gender: 1, relation: "", selectedImage: "", selectedImagePreview: "" });
          setShowEditModal(false);
        } else {
          toast.error(res.data?.message);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleDeletePatient = () => {};

  return (
    <Wrapper>
      <Section topMd={100} topLg={0} topXl={110} bottomMd={200} bottomLg={150} bottomXl={110}>
        {loading && !patientData && <FullPageLoader />}
        {!loading && patientData && (
          <div className="content-wrapper">
            <div className="row">
              <div className="d-flex align-items-center">
                <CommonMobileHeader
                  title={"Manage Patients"}
                  canBack={true}
                  trailing={
                    <button type="button" className="bg-success-surface rounded" onClick={onAddPatientClick}>
                      Add Patient
                    </button>
                  }
                />
              </div>
              <div className="col-md-6"></div>
              <div className="col-md-6 g-2 px-3">
                {!loading && userDetail && (
                  <div className="row pl-0 g-2">
                    <div className="px-2 py-2 bg-grey2 d-flex flex-column">
                      <span className="text-black fw-medium fs-normal mb-0">{userDetail?.fullname}</span>
                      <span className="text-grey2 fs-md">Myself</span>
                    </div>
                    {patientData.map((patient) => (
                      <div id={patient.id} className="d-flex justify-content-between bg-grey2">
                        <div className="px-2 py-2 b d-flex flex-column">
                          <span className="text-black fw-medium fs-normal mb-0">{patient?.fullname}</span>
                          <span className="text-grey2 fs-md">{patient.relation}</span>
                        </div>
                        <div type="button" className="bg-transparent d-flex align-items-center gap-1 p-2">
                          <button className="bg-warning-surface rounded py-1 px-3" onClick={() => onEditPatientClick(patient)}>
                            Edit
                          </button>
                          <button className="bg-danger-surface rounded py-1 px-3" onClick={onDeletePatientClick}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <LoadMore visible={showLoadMore} loading={loading} onLoadMoreClick={fetchPatientsData} />
              </div>
            </div>
            {/* Edit Patient Modal */}
            <MyModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} modalTitle={"Edit Patient"}>
              <div className="col-4">
                <button type="button" onClick={onEditPatientImageClick} className="bg-grey2 img-thumbnail d-flex justify-content-center align-items-center mb-3 rounded mt-3 position-relative" style={{ height: "100px", width: "100px", cursor: "pointer" }}>
                  <input type="file" hidden accept="image/*" ref={editPatientFileRef} onChange={handleEditPatientImageChange} />
                  {editPatientData?.selectedImagePreview && <img className="h-100 w-100" src={editPatientData?.selectedImagePreview} style={{ objectFit: "cover" }} />}
                  <FaCirclePlus color="gray" size={24} className="position-absolute top-45 inset-0 z-2" />
                </button>
              </div>
              <InputField label="Full Name" placeholder="Enter full name" inputType={"text"} value={editPatientData?.fullname} onChange={(e) => setEditPatientData({ ...editPatientData, fullname: e.target.value })} />
              <InputField label="Age" placeholder="Enter age" inputType={"numeric"} value={editPatientData?.age} onChange={(e) => setEditPatientData({ ...editPatientData, age: e.target.value })} />
              <SelectField
                label={"Select Gender"}
                items={[
                  { label: "Male", value: 1 },
                  { label: "Female", value: 0 },
                ]}
                onChange={(e) => setEditPatientData({ ...editPatientData, gender: e.target.value })}
              />
              <InputField label="Relation" placeholder="Enter relation" inputType={"text"} value={editPatientData?.relation} onChange={(e) => setEditPatientData({ ...editPatientData, relation: e.target.value })} />
              <SecondaryButton title={"Continue"} onClick={updatePatient} loading={submitting} />
            </MyModal>
            {/* Add Patient Modal */}
            <MyModal isOpen={showAddPatientModal} onClose={() => setShowAddPatientModal(false)} modalTitle={"Add Patient"}>
              <div className="col-4">
                <button type="button" onClick={onAddPatientImageClick} className="bg-grey2 img-thumbnail d-flex justify-content-center align-items-center mb-3 rounded mt-3 position-relative" style={{ height: "100px", width: "100px", cursor: "pointer" }}>
                  <input type="file" hidden accept="image/*" ref={addPatientFileRef} onChange={handleAddPatientImageChange} />
                  {addPatientData?.selectedImagePreview && <img className="h-100 w-100" src={addPatientData?.selectedImagePreview} style={{ objectFit: "cover" }} />}
                  <FaCirclePlus color="gray" size={24} className="position-absolute top-45 inset-0 z-2" />
                </button>
              </div>
              <InputField label="Full Name" placeholder="Enter full name" inputType={"text"} value={addPatientData?.fullname} onChange={(e) => setAddPatientData({ ...addPatientData, fullname: e.target.value })} />
              <InputField label="Age" placeholder="Enter age" inputType={"numeric"} value={editPatientData?.age} onChange={(e) => setAddPatientData({ ...addPatientData, age: e.target.value })} />
              <SelectField
                label={"Select Gender"}
                items={[
                  { label: "Male", value: 1 },
                  { label: "Female", value: 0 },
                ]}
                onChange={(e) => setAddPatientData({ ...addPatientData, gender: e.target.value })}
              />
              <InputField label="Relation" placeholder="Enter relation" inputType={"text"} value={addPatientData?.relation} onChange={(e) => setAddPatientData({ ...addPatientData, relation: e.target.value })} />
              <SecondaryButton title={"Continue"} onClick={saveNewPatient} loading={submitting} />
            </MyModal>
          </div>
        )}
      </Section>
    </Wrapper>
  );
}
const Wrapper = styled.div``;
